import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { ENTITY_TYPES } from '@sales-center/common'
import { styles } from '@user/components/SalesTeam/ReAssignSalesTeam/useStyles'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import { ICrossBranchReassign } from '@user/store/lead/typings'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'

interface Props {
  entityType: ENTITY_TYPES
  branch: string
  crossBranchValidation?: ICrossBranchReassign
  onChange?: (checked: boolean) => void
}

export function ReAssignSalesTeamAlert(props: Props) {
  const { crossBranchValidation, branch, entityType, onChange } = props
  const [removeServices, setRemoveServices] = useState(false)

  const isOpportunity = entityType === ENTITY_TYPES.Opportunity
  const showMatchedWarning = isOpportunity || crossBranchValidation?.isMatched

  const showRemoveServices = useMemo(() => {
    return (
      onChange &&
      !isOpportunity &&
      crossBranchValidation?.hasServices &&
      !crossBranchValidation?.isMatched
    )
  }, [
    onChange,
    isOpportunity,
    crossBranchValidation?.hasServices,
    crossBranchValidation?.isMatched,
  ])

  const onCheckedUnChecked = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setRemoveServices(checked)

      if (onChange) {
        onChange(checked)
      }
    },
    [onChange],
  )

  const BranchAlertText = useCallback(() => {
    return (
      <Typography variant="subtitle2" sx={styles.alertInfo}>
        {branch}
      </Typography>
    )
  }, [branch])

  const getMessageContent = useCallback(() => {
    if (isOpportunity) {
      return (
        <>
          The Sales Team dropdown below has been restricted because this{' '}
          {' ' + entityType.toLowerCase() + ' '}
          is in the <BranchAlertText />. It can only be re-assigned within the{' '}
          <BranchAlertText /> to ensure service and/or location information is
          accurate.
        </>
      )
    }
    if (crossBranchValidation?.isMatched) {
      return (
        <>
          The Sales Team dropdown below has been restricted because this
          {' ' + entityType.toLowerCase()} is in the <BranchAlertText />. It can
          only be re-assigned within the <BranchAlertText /> because there is an
          associated location in PestPac.
        </>
      )
    }
    if (crossBranchValidation?.hasServices) {
      return (
        <>
          The Sales Team dropdown below has been restricted because this lead is
          in the <BranchAlertText />. Remove the services to re-assign this lead
          to a different branch. (You may want to also remove any open
          agreements).
        </>
      )
    }
    return null
  }, [isOpportunity, entityType, crossBranchValidation, BranchAlertText])

  const RemoveServicesText = useCallback(() => {
    return (
      <Typography variant="body1">
        Remove Services to allow cross branch reassign <br />
        (We will save Service Details as a note)
      </Typography>
    )
  }, [])

  const ReAssignAlertText = useCallback(() => {
    return (
      <Typography variant="body2" component="span">
        {getMessageContent()}
      </Typography>
    )
  }, [getMessageContent])

  return (
    <>
      <Alert
        severity={showMatchedWarning ? 'warning' : 'info'}
        sx={styles.alert}
        style={{ marginBottom: showRemoveServices ? '10px' : '20px' }}
      >
        <ReAssignAlertText />
      </Alert>
      {showRemoveServices && (
        <Box mr={2} ml={1} mb={1}>
          <FormControlLabel
            data-test-id="removeServicesCheckbox"
            label={<RemoveServicesText />}
            control={
              <Checkbox
                color="primary"
                checked={removeServices}
                onChange={onCheckedUnChecked}
              />
            }
          />
        </Box>
      )}
    </>
  )
}
